import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import MentionAutocomplete from "../components/MentionAutocomplete";
import UserNameLink from "../components/UserNameLink";
import ReportButton from "../components/ReportButton";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { MessagesSquare, Plus, ArrowLeft, MessageCircle, Trash2, Send, Shield, Clock, Lock, Unlock, Megaphone, Pin, PinOff, Pencil, Check, X as XIcon, Tag, Reply as ReplyIcon, CornerDownRight, BookOpenCheck, Search, Video, Paperclip, Link as LinkIcon } from "lucide-react";
import PresenceDot from "../components/PresenceDot";
import PageHero from "../components/PageHero";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import EmojiPickerButton, { insertAtCursor } from "../components/EmojiPickerButton";
import ReactionBar, { RECAP_REACTIONS } from "../components/ReactionBar";
import StreakLeaderboard from "../components/StreakLeaderboard";
import VideoEmbed, { detectVideo } from "../components/VideoEmbed";
import { AttachmentChip, TopicAttachments } from "../components/TopicAttachments";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "just now";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff) || diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function Avatar({ name, role, avatarUrl, size = 36 }) {
  const letter = (name || "?").trim()[0]?.toUpperCase() || "?";
  const isAdmin = role === "admin";
  const isMod = role === "moderator";
  const style = { width: size, height: size };
  const borderCls = isAdmin ? "border-[#0D9488]/60"
    : isMod ? "border-emerald-600/60"
    : "border-slate-300";
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name || "avatar"}
        style={style}
        className={`object-cover border shrink-0 ${borderCls}`}
        title={name}
      />
    );
  }
  return (
    <div
      style={style}
      className={`flex items-center justify-center border font-display font-black text-sm shrink-0 ${
        isAdmin ? "border-[#0D9488]/60 bg-[#0D9488]/10 text-[#0D9488]"
        : isMod ? "border-emerald-600/60 bg-emerald-950/40 text-emerald-400"
        : "border-slate-300 bg-slate-50 text-slate-800"
      }`}
      title={name}
    >
      {letter}
    </div>
  );
}

function RoleBadge({ role }) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/40 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest">
        <Shield className="w-2.5 h-2.5" /> Admin
      </span>
    );
  }
  if (role === "moderator") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-700/60 text-emerald-400 font-mono-num text-[9px] uppercase tracking-widest">
        <Shield className="w-2.5 h-2.5" /> Mod
      </span>
    );
  }
  return null;
}

// ============ Community Topic List ============
function highlightMatches(text, query) {
  if (!text || !query) return text;
  const parts = String(text).split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase()
      ? <mark key={`hl-${i}-${p}`} className="bg-[#0D9488]/25 text-[#0D9488] px-0.5">{p}</mark>
      : <span key={`hl-${i}-${p}`}>{p}</span>
  );
}

function snippet(text, query, radius = 90) {
  if (!text) return "";
  const t = String(text);
  const idx = t.toLowerCase().indexOf((query || "").toLowerCase());
  if (idx < 0 || !query) return t.slice(0, radius * 2) + (t.length > radius * 2 ? "…" : "");
  const start = Math.max(0, idx - radius);
  const end = Math.min(t.length, idx + query.length + radius);
  return (start > 0 ? "…" : "") + t.slice(start, end) + (end < t.length ? "…" : "");
}

function SearchResultsPane({ results, query, searching, catMap }) {
  const totalTopics = results?.topics?.length || 0;
  const totalReplies = results?.replies?.length || 0;
  const total = totalTopics + totalReplies;

  return (
    <div className="space-y-6" data-testid="community-search-results">
      <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2 flex-wrap">
        <span>// Search results for "<span className="text-[#0D9488]">{query}</span>"</span>
        <span className="text-slate-400">·</span>
        <span>{total} {total === 1 ? "hit" : "hits"}</span>
        {searching && <span className="text-[#0D9488]">· refreshing…</span>}
      </div>

      {total === 0 && !searching && (
        <div className="border border-slate-200 p-12 text-center" data-testid="community-search-empty">
          <MessagesSquare className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="font-display font-black uppercase text-2xl text-slate-500">No matches</div>
          <p className="text-slate-500 mt-1 text-sm">Try a different keyword or check spelling.</p>
        </div>
      )}

      {totalTopics > 0 && (
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 pb-2 border-b border-slate-200 mb-2">
            // Topics · {totalTopics}
          </div>
          <div className="border border-slate-200 bg-white divide-y divide-zinc-900">
            {results.topics.map((t) => {
              const cat = t.category_id ? catMap[t.category_id] : null;
              return (
                <Link
                  key={t.id}
                  to={`/community/${t.id}`}
                  className="block p-4 lg:p-5 hover:bg-slate-50/40 transition-colors group"
                  data-testid={`search-topic-${t.id}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900 group-hover:text-[#0D9488] transition-colors">
                      {highlightMatches(t.title, query)}
                    </div>
                    {cat && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-num text-[9px] uppercase tracking-widest"
                        style={{ borderColor: cat.color, color: cat.color }}
                      >
                        <span className="inline-block w-1.5 h-1.5" style={{ background: cat.color }} />
                        {cat.name}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                    {highlightMatches(snippet(t.body, query), query)}
                  </div>
                  <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2 flex items-center gap-2">
                    <span>{t.author_name}</span>
                    <span className="text-zinc-700">·</span>
                    <span>{timeAgo(t.last_reply_at || t.created_at)}</span>
                    <span className="text-zinc-700">·</span>
                    <span>{t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {totalReplies > 0 && (
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 pb-2 border-b border-slate-200 mb-2">
            // Replies · {totalReplies}
          </div>
          <div className="border border-slate-200 bg-white divide-y divide-zinc-900">
            {results.replies.map(({ reply, topic }) => (
              <Link
                key={reply.id}
                to={`/community/${topic.id}`}
                className="block p-4 lg:p-5 hover:bg-slate-50/40 transition-colors group"
                data-testid={`search-reply-${reply.id}`}
              >
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-2">
                  <CornerDownRight className="w-3 h-3" />
                  <span>Reply in</span>
                  <span className="text-slate-800 normal-case tracking-normal font-sans">{topic.title}</span>
                </div>
                <div className="text-sm text-slate-800 leading-relaxed line-clamp-3">
                  {highlightMatches(snippet(reply.body, query), query)}
                </div>
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2 flex items-center gap-2">
                  <span>{reply.author_name}</span>
                  <span className="text-zinc-700">·</span>
                  <span>{timeAgo(reply.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunityList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [authorPresence, setAuthorPresence] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all"); // "all" | id | "none"
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newAttachments, setNewAttachments] = useState([]);
  const [linkDraft, setLinkDraft] = useState({ open: false, url: "", title: "" });
  const fileInputRef = useRef(null);
  const [posting, setPosting] = useState(false);
  const [lockOnPost, setLockOnPost] = useState(false);
  const [pinOnPost, setPinOnPost] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const pollRef = useRef(null);
  const newBodyRef = useRef(null);
  // Community-wide search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // { topics, replies } | null
  const [searching, setSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  const [sourceFilter, setSourceFilter] = useState(null); // null | "weekly_recap"
  const [sortMode, setSortMode] = useState(null);         // null | "trending"
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from ?tag=weekly-recap and keep the URL in sync so it can be linked/shared.
  useEffect(() => {
    const tag = searchParams.get("tag");
    if (tag === "weekly-recap" && sourceFilter !== "weekly_recap") {
      setSourceFilter("weekly_recap");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const current = searchParams.get("tag");
    if (sourceFilter === "weekly_recap" && current !== "weekly-recap") {
      setSearchParams({ tag: "weekly-recap" }, { replace: true });
    } else if (!sourceFilter && current) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const load = useCallback(async () => {
    try {
      const topicsParams = {};
      if (sourceFilter) topicsParams.source = sourceFilter;
      if (sortMode) topicsParams.sort = sortMode;
      const [tRes, cRes] = await Promise.all([
        api.get("/community/topics", { params: topicsParams }),
        api.get("/community/categories"),
      ]);
      setTopics(tRes.data);
      setCategories(cRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, sortMode]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 10_000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Bulk-fetch presence for every visible topic author, refresh every 30s
  useEffect(() => {
    const ids = Array.from(new Set(topics.map((t) => t.user_id).filter(Boolean)));
    if (ids.length === 0) { setAuthorPresence({}); return; }
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const { data } = await api.post("/presence/lookup", { user_ids: ids });
        if (cancelled) return;
        const map = {};
        (data?.items || []).forEach((r) => { map[r.user_id] = r.status; });
        setAuthorPresence(map);
      } catch { /* silent */ }
    };
    fetchPresence();
    const t = setInterval(fetchPresence, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [topics]);

  // Debounced search — hits /api/community/search after 300ms idle
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/community/search", { params: { q } });
        setSearchResults(data);
      } catch (err) {
        toast.error(formatApiError(err));
        setSearchResults({ topics: [], replies: [] });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  const visibleTopics = useMemo(() => {
    if (activeCategory === "all") return topics;
    if (activeCategory === "none") return topics.filter((t) => !t.category_id);
    return topics.filter((t) => t.category_id === activeCategory);
  }, [topics, activeCategory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setPosting(true);
    try {
      const isAdmin = user?.role === "admin";
      const res = await api.post("/community/topics", {
        title,
        body,
        locked: isAdmin ? lockOnPost : false,
        pinned: isAdmin ? pinOnPost : false,
        category_id: newCategoryId || null,
        video_url: newVideoUrl.trim() || null,
        attachments: newAttachments,
      });
      toast.success(
        pinOnPost && lockOnPost ? "Announcement pinned + locked"
        : pinOnPost ? "Topic pinned to top"
        : lockOnPost ? "Announcement posted (locked)"
        : "Topic posted"
      );
      setOpenNew(false);
      setTitle("");
      setBody("");
      setNewCategoryId("");
      setNewVideoUrl("");
      setNewAttachments([]);
      setLinkDraft({ open: false, url: "", title: "" });
      setLockOnPost(false);
      setPinOnPost(false);
      // Optimistically jump into the new topic
      navigate(`/community/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/community/topics/${confirmDelete.id}`);
      toast.success("Topic deleted");
      setTopics((t) => t.filter((x) => x.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canDelete = (t) => user?.id === t.user_id || user?.role === "admin" || user?.role === "moderator";

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("File must be under 3 MB"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setNewAttachments((a) => [...a, {
        type: "file", filename: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size, data_url: ev.target.result,
      }]);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const addLinkAttachment = () => {
    const url = linkDraft.url.trim();
    if (!url) return;
    try { new URL(url); } catch { toast.error("Enter a valid URL (with https://)"); return; }
    setNewAttachments((a) => [...a, { type: "link", url, title: linkDraft.title.trim() || url }]);
    setLinkDraft({ open: false, url: "", title: "" });
  };
  const removeAttachment = (idx) => setNewAttachments((a) => a.filter((_, i) => i !== idx));

  return (
    <AppLayout>
      <PageHero
        eyebrow="Community"
        title="Discussion"
        subtitle="Compare protocols, ask questions, share what's working. Auto-refreshes every 10s. Be respectful — this is not medical advice."
        icon={<MessagesSquare className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <Button
            onClick={() => setOpenNew(true)}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 px-6 uppercase tracking-widest font-semibold"
            data-testid="community-new-topic"
          >
            <Plus className="w-4 h-4 mr-2" /> New Topic
          </Button>
        }
      />

      {/* Community-wide search bar */}
      <div className="mb-4" data-testid="community-search-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search topics and replies…"
            className="pl-9 pr-24 bg-slate-50 border-slate-200 rounded-none h-11 focus-visible:ring-0 focus-visible:border-[#0D9488]"
            data-testid="community-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {searching && (
              <span className="font-mono-num text-[10px] uppercase tracking-widest text-[#0D9488]" data-testid="community-search-loading">Searching…</span>
            )}
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="text-slate-500 hover:text-white"
                data-testid="community-search-clear"
                aria-label="Clear search"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category filter chips (hidden while searching) */}
      {!searchResults && (
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="community-category-chips">
        <button
          type="button"
          onClick={() => { setActiveCategory("all"); setSourceFilter(null); }}
          className={`px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] border transition-colors ${
            activeCategory === "all" && !sourceFilter
              ? "bg-[#0D9488] border-[#0D9488] text-black"
              : "bg-slate-50 border-slate-200 text-slate-600 hover:text-white hover:border-slate-300"
          }`}
          data-testid="community-category-chip-all"
        >
          All · {topics.length}
        </button>
        {/* Weekly Recaps filter — public feed of every user's shared rollup */}
        <button
          type="button"
          onClick={() => { setSourceFilter(sourceFilter === "weekly_recap" ? null : "weekly_recap"); setSortMode(null); setActiveCategory("all"); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] border transition-colors ${
            sourceFilter === "weekly_recap"
              ? "border-teal-400 bg-teal-950/60 text-teal-300"
              : "bg-slate-50 border-slate-200 text-slate-600 hover:text-teal-300 hover:border-teal-700"
          }`}
          data-testid="community-source-chip-weekly-recap"
          title="Show only Weekly Recap posts"
        >
          <span aria-hidden>🔥</span> Weekly Recaps
        </button>
        {/* Trending sort — only meaningful within the weekly-recap feed */}
        {sourceFilter === "weekly_recap" && (
          <button
            type="button"
            onClick={() => setSortMode(sortMode === "trending" ? null : "trending")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] border transition-colors ${
              sortMode === "trending"
                ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:text-[#0D9488] hover:border-[#0F766E]"
            }`}
            data-testid="community-sort-trending"
            title="Sort by most-cheered in the last 7 days"
          >
            <span aria-hidden>📈</span> Trending
          </button>
        )}
        {categories.map((c) => {
          const count = topics.filter((t) => t.category_id === c.id).length;
          const active = activeCategory === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveCategory(c.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                active
                  ? "text-black border-transparent"
                  : "bg-slate-50 border-slate-200 text-slate-600 hover:text-white hover:border-slate-300"
              }`}
              style={active ? { background: c.color } : undefined}
              title={c.description}
              data-testid={`community-category-chip-${c.id}`}
            >
              <span className={`inline-block w-2 h-2 ${active ? "bg-black" : ""}`} style={!active ? { background: c.color } : undefined} />
              {c.name} · {count}
            </button>
          );
        })}
        {topics.some((t) => !t.category_id) && (
          <button
            type="button"
            onClick={() => setActiveCategory("none")}
            className={`px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-[0.25em] border transition-colors ${
              activeCategory === "none"
                ? "bg-zinc-200 border-zinc-200 text-black"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:text-white hover:border-slate-300"
            }`}
            data-testid="community-category-chip-none"
          >
            Uncategorized · {topics.filter((t) => !t.category_id).length}
          </button>
        )}
      </div>
      )}

      {/* Leaderboard — appears only when the Weekly Recaps filter is active */}
      {!searchResults && sourceFilter === "weekly_recap" && <StreakLeaderboard />}

      {searchResults ? (
        <SearchResultsPane
          results={searchResults}
          query={searchQuery.trim()}
          searching={searching}
          catMap={catMap}
        />
      ) : loading ? (
        <div className="border border-slate-200 p-16 text-center font-mono-num text-slate-500 uppercase tracking-widest text-xs" data-testid="community-loading">
          Loading discussion…
        </div>
      ) : visibleTopics.length === 0 ? (
        <div className="border border-slate-200 p-16 text-center" data-testid="community-empty">
          <MessagesSquare className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <div className="font-display font-black text-3xl uppercase text-slate-500">
            {activeCategory === "all" ? "Silence in the vault" : "Empty category"}
          </div>
          <p className="text-slate-500 mt-2 text-sm">
            {activeCategory === "all"
              ? "Be the first to start a discussion."
              : "No topics here yet. Start one — or pick another category."}
          </p>
          <Button
            onClick={() => setOpenNew(true)}
            className="mt-6 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
            data-testid="community-empty-cta"
          >
            <Plus className="w-4 h-4 mr-2" /> {activeCategory === "all" ? "Start the first topic" : "Start a topic here"}
          </Button>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white divide-y divide-zinc-900" data-testid="community-topic-list">
          {visibleTopics.map((t) => {
            const cat = t.category_id ? catMap[t.category_id] : null;
            return (
            <div
              key={t.id}
              className={`p-4 lg:p-5 hover:bg-slate-50/40 transition-colors group flex items-start gap-4 ${t.pinned ? "bg-[#0D9488]/[0.04]" : ""}`}
              data-testid={`community-topic-${t.id}`}
            >
              <UserNameLink userId={t.user_id} handle={t.author_handle} name={t.author_name} testid={`community-topic-avatar-link-${t.id}`}>
                <Avatar name={t.author_name} role={t.author_role} avatarUrl={t.author_avatar_url} />
              </UserNameLink>
              <Link to={`/community/${t.id}`} className="flex-1 min-w-0" data-testid={`community-topic-link-${t.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {t.pinned && (
                    <Pin className="w-3.5 h-3.5 text-[#0D9488] shrink-0" data-testid={`community-topic-pinned-${t.id}`} />
                  )}
                  <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900 group-hover:text-[#0D9488] transition-colors truncate">
                    {t.title}
                  </div>
                  <RoleBadge role={t.author_role} />
                  {cat && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-num text-[9px] uppercase tracking-widest"
                      style={{ borderColor: cat.color, color: cat.color }}
                      data-testid={`community-topic-category-${t.id}`}
                    >
                      <Tag className="w-2.5 h-2.5" /> {cat.name}
                    </span>
                  )}
                  {t.pinned && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/50 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest"
                    >
                      <Pin className="w-2.5 h-2.5" /> Pinned
                    </span>
                  )}
                  {t.locked && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/50 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest"
                      data-testid={`community-topic-locked-${t.id}`}
                    >
                      <Lock className="w-2.5 h-2.5" /> Locked
                    </span>
                  )}
                </div>
                <div className="text-slate-600 text-sm line-clamp-2 mb-2">{t.body}</div>
                <div className="flex items-center gap-4 text-[11px] font-mono-num uppercase tracking-widest text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1.5">
                    {t.user_id && <PresenceDot userId={t.user_id} statusMap={authorPresence} />}
                    <UserNameLink userId={t.user_id} handle={t.author_handle} name={t.author_name} prefix="@" className="text-slate-500 hover:text-[#0D9488]" showUnderline={false} />
                  </span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(t.created_at)}</span>
                  <span className="flex items-center gap-1 text-[#0D9488]">
                    <MessageCircle className="w-3 h-3" />{t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}
                  </span>
                  {t.reply_count > 0 && (
                    <span className="text-slate-400">· active {timeAgo(t.last_reply_at)}</span>
                  )}
                </div>
              </Link>
              {canDelete(t) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.preventDefault(); setConfirmDelete(t); }}
                  className="opacity-0 group-hover:opacity-100 h-8 rounded-none text-slate-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 shrink-0"
                  data-testid={`community-topic-delete-${t.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-xl" data-testid="community-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">Start a Topic</DialogTitle>
            <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">
              // share protocols, ask questions, compare notes
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="community-new-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Category</Label>
              <Select value={newCategoryId || "__none__"} onValueChange={(v) => setNewCategoryId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="community-new-category">
                  <SelectValue placeholder="Pick a category (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  <SelectItem value="__none__">
                    <span className="text-slate-600">— Uncategorized —</span>
                  </SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`community-new-category-option-${c.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2 h-2" style={{ background: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={140}
                placeholder="e.g. Anyone splitting BPC into 2× daily?"
                className="bg-slate-50 border-slate-200 rounded-none h-11"
                data-testid="community-new-title"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Body</Label>
              <div className="relative">
                <Textarea
                  ref={newBodyRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                  maxLength={8000}
                  placeholder="Share what you're doing, what you're seeing, what you'd try next…"
                  className="bg-slate-50 border-slate-200 rounded-none resize-none pr-12"
                  data-testid="community-new-body"
                  required
                />
                <div className="absolute right-2 top-2">
                  <EmojiPickerButton
                    testid="emoji-new-body"
                    onPick={(e) => insertAtCursor(newBodyRef.current, body, e, setBody)}
                  />
                </div>
              </div>
              <div className="font-mono-num text-[10px] text-slate-500 text-right">{body.length} / 8000</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                <Video className="w-3 h-3" /> Video link (optional)
              </Label>
              <Input
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                placeholder="Paste a YouTube, Vimeo or Rumble URL"
                maxLength={500}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-xs"
                data-testid="community-new-video"
              />
              {newVideoUrl.trim() && (() => {
                const info = detectVideo(newVideoUrl.trim());
                if (info) {
                  return (
                    <div className="font-mono-num text-[10px] uppercase tracking-widest text-[#0D9488]" data-testid="community-new-video-detected">
                      ✓ {info.platform} detected {info.embed ? "· will embed inline" : "· will show as open-link card"}
                    </div>
                  );
                }
                return (
                  <div className="font-mono-num text-[10px] uppercase tracking-widest text-red-400" data-testid="community-new-video-invalid">
                    ! Not a recognized YouTube / Vimeo / Rumble URL
                  </div>
                );
              })()}
            </div>
            {user?.role === "admin" && (
              <div className="space-y-2">
                <label
                >
                  <input
                    type="checkbox"
                    checked={pinOnPost}
                    onChange={(e) => setPinOnPost(e.target.checked)}
                    className="mt-0.5 accent-[#0D9488]"
                    data-testid="community-new-pin-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Pin className="w-3.5 h-3.5 text-[#0D9488]" />
                      <span className="font-mono-num text-[11px] uppercase tracking-[0.2em] text-slate-800">Pin to top</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Sticks this topic above every non-pinned post. New topics appear underneath.
                    </div>
                  </div>
                </label>
                <label
                  className="flex items-start gap-3 p-3 border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300 transition-colors"
                  data-testid="community-new-lock-row"
                >
                  <input
                    type="checkbox"
                    checked={lockOnPost}
                    onChange={(e) => setLockOnPost(e.target.checked)}
                    className="mt-0.5 accent-[#0D9488]"
                    data-testid="community-new-lock-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-3.5 h-3.5 text-[#0D9488]" />
                      <span className="font-mono-num text-[11px] uppercase tracking-[0.2em] text-slate-800">Post as announcement</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Locks the topic so nobody (except an admin) can reply. Great for house rules, updates, or FAQ posts.
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Skool-style attachment toolbar */}
            <div className="border-t border-slate-200 pt-3 -mx-1" data-testid="composer-toolbar">
              <div className="flex items-center gap-1 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.doc,.docx,.xls,.xlsx"
                  onChange={handleFilePick}
                  className="hidden"
                  data-testid="composer-file-input"
                />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="w-9 h-9 flex items-center justify-center text-slate-600 hover:text-[#0D9488] hover:bg-slate-50 transition-colors"
                  title="Attach a file (PDF, image, doc) — up to 3 MB"
                  data-testid="composer-attach-file">
                  <Paperclip className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setLinkDraft((d) => ({ ...d, open: !d.open }))}
                  className={`w-9 h-9 flex items-center justify-center transition-colors ${linkDraft.open ? "text-[#0D9488] bg-slate-50" : "text-slate-600 hover:text-[#0D9488] hover:bg-slate-50"}`}
                  title="Attach a link"
                  data-testid="composer-attach-link">
                  <LinkIcon className="w-4 h-4" />
                </button>
                <div className="text-[10px] font-mono-num text-slate-400 uppercase tracking-widest ml-2 hidden sm:inline">
                  Attach · PDF, image, doc, or URL
                </div>
              </div>

              {linkDraft.open && (
                <div className="mt-3 border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="composer-link-form">
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr] gap-2">
                    <Input value={linkDraft.title} onChange={(e) => setLinkDraft((d) => ({ ...d, title: e.target.value }))}
                      placeholder="Link title (optional)"
                      className="bg-black border-slate-200 rounded-none h-10 text-sm"
                      data-testid="composer-link-title" />
                    <Input value={linkDraft.url} onChange={(e) => setLinkDraft((d) => ({ ...d, url: e.target.value }))}
                      placeholder="https://example.com"
                      className="bg-black border-slate-200 rounded-none h-10 text-sm font-mono-num"
                      data-testid="composer-link-url" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setLinkDraft({ open: false, url: "", title: "" })}
                      className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800 h-8 text-xs">Cancel</Button>
                    <Button type="button" onClick={addLinkAttachment}
                      className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-8 text-xs uppercase tracking-widest font-semibold px-4"
                      data-testid="composer-link-add">Add link</Button>
                  </div>
                </div>
              )}

              {newAttachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="composer-attachments-list">
                  {newAttachments.map((att, i) => (
                    <AttachmentChip key={`${att?.name || "att"}-${att?.size || 0}-${i}`} att={att} onRemove={() => removeAttachment(i)} />
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenNew(false)}
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                data-testid="community-new-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={posting}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                data-testid="community-new-submit">
                {posting ? "Posting…" : "Post Topic"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900" data-testid="community-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete topic?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              "{confirmDelete?.title}" and all {confirmDelete?.reply_count || 0} {(confirmDelete?.reply_count || 0) === 1 ? "reply" : "replies"} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="community-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="community-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin: Category Manager — accessed from Site Settings */}
    </AppLayout>
  );
}

// ============ Community Topic Detail ============
export function CommunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState(false);
  const [confirmDeleteReply, setConfirmDeleteReply] = useState(null);
  // Edit state
  const [editingTopic, setEditingTopic] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [savingTopic, setSavingTopic] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyBody, setEditReplyBody] = useState("");
  const [savingReply, setSavingReply] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // reply object being replied to, or null for top-level
  const pollRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const lastCountRef = useRef(0);
  const replyRef = useRef(null);
  const editTopicRef = useRef(null);
  const editReplyRef = useRef(null);

  const topicCategory = useMemo(() => {
    if (!topic?.category_id) return null;
    return categories.find((c) => c.id === topic.category_id) || null;
  }, [topic, categories]);

  const load = useCallback(async (opts = {}) => {
    try {
      const [tRes, cRes] = await Promise.all([
        api.get(`/community/topics/${id}`),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/community/categories"),
      ]);
      setTopic(tRes.data.topic);
      setReplies(tRes.data.replies);
      if (cRes?.data && !categories.length) setCategories(cRes.data);
      // Auto-scroll if new replies arrived
      if (opts.silent && tRes.data.replies.length > lastCountRef.current) {
        setTimeout(() => scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
      }
      lastCountRef.current = tRes.data.replies.length;
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Topic not found");
        navigate("/community");
        return;
      }
      if (!opts.silent) toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, categories]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => load({ silent: true }), 10_000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const canDeleteTopic = topic && (user?.id === topic.user_id || isStaff);
  const canEditTopic = topic && (user?.id === topic.user_id || isStaff);
  const canLockTopic = topic && isStaff;
  const canPinTopic = topic && isStaff;
  const canReply = topic && (!topic.locked || isStaff);
  const canDeleteReply = (r) => user?.id === r.user_id || isStaff;
  const canEditReply = (r) => user?.id === r.user_id || isStaff;

  const startEditTopic = () => {
    if (!topic) return;
    setEditTitle(topic.title);
    setEditBody(topic.body);
    setEditingTopic(true);
  };

  const cancelEditTopic = () => {
    setEditingTopic(false);
    setEditTitle("");
    setEditBody("");
  };

  const saveEditTopic = async () => {
    if (!editTitle.trim() || !editBody.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSavingTopic(true);
    try {
      const res = await api.patch(`/community/topics/${id}`, { title: editTitle, body: editBody });
      setTopic(res.data);
      setEditingTopic(false);
      toast.success("Topic updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingTopic(false);
    }
  };

  const startEditReply = (r) => {
    setEditingReplyId(r.id);
    setEditReplyBody(r.body);
  };

  const cancelEditReply = () => {
    setEditingReplyId(null);
    setEditReplyBody("");
  };

  const saveEditReply = async () => {
    if (!editReplyBody.trim()) {
      toast.error("Reply body is required");
      return;
    }
    setSavingReply(true);
    try {
      const res = await api.patch(`/community/topics/${id}/replies/${editingReplyId}`, { body: editReplyBody });
      setReplies((rs) => rs.map((x) => x.id === res.data.id ? res.data : x));
      setEditingReplyId(null);
      setEditReplyBody("");
      toast.success("Reply updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingReply(false);
    }
  };

  const toggleLock = async () => {
    if (!topic) return;
    try {
      const res = await api.patch(`/community/topics/${id}/lock`, { locked: !topic.locked });
      setTopic(res.data);
      toast.success(res.data.locked ? "Topic locked" : "Topic unlocked");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePin = async () => {
    if (!topic) return;
    try {
      const res = await api.patch(`/community/topics/${id}/pin`, { pinned: !topic.pinned });
      setTopic(res.data);
      toast.success(res.data.pinned ? "Pinned to top" : "Unpinned");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setMentorship = async (nextTopic) => {
    if (!topic) return;
    try {
      const res = await api.patch(`/community/topics/${id}/mentorship`, { topic: nextTopic });
      setTopic(res.data);
      toast.success(nextTopic ? `Marked as ${nextTopic} mentorship` : "Mentorship flag removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    try {
      const payload = { body: reply };
      if (replyingTo?.id) payload.parent_reply_id = replyingTo.id;
      const res = await api.post(`/community/topics/${id}/replies`, payload);
      setReplies((r) => [...r, res.data]);
      setTopic((t) => t ? { ...t, reply_count: (t.reply_count || 0) + 1, last_reply_at: res.data.created_at } : t);
      setReply("");
      setReplyingTo(null);
      setTimeout(() => scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  const startReplyTo = (r) => {
    setReplyingTo(r);
    setTimeout(() => {
      replyRef.current?.focus();
      replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const doDeleteTopic = async () => {
    try {
      await api.delete(`/community/topics/${id}`);
      toast.success("Topic deleted");
      navigate("/community");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDeleteReply = async () => {
    if (!confirmDeleteReply) return;
    try {
      await api.delete(`/community/topics/${id}/replies/${confirmDeleteReply.id}`);
      setReplies((r) => r.filter((x) => x.id !== confirmDeleteReply.id));
      setTopic((t) => t ? { ...t, reply_count: Math.max(0, (t.reply_count || 0) - 1) } : t);
      setConfirmDeleteReply(null);
      toast.success("Reply deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppLayout>
      <Link
        to="/community"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-white text-xs font-mono-num uppercase tracking-widest mb-6"
        data-testid="community-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to discussion
      </Link>

      {loading ? (
        <div className="border border-slate-200 p-16 text-center font-mono-num text-slate-500 uppercase tracking-widest text-xs">
          Loading…
        </div>
      ) : topic ? (
        <>
          {/* Topic head */}
          <div className="border border-slate-200 bg-white p-5 lg:p-6 mb-6" data-testid="community-topic-head">
            <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
              <UserNameLink userId={topic.user_id} handle={topic.author_handle} name={topic.author_name} testid="community-topic-head-avatar-link">
                <Avatar name={topic.author_name} role={topic.author_role} avatarUrl={topic.author_avatar_url} />
              </UserNameLink>
              <div className="flex-1 min-w-0">
                {editingTopic ? (
                  <div className="space-y-3" data-testid="community-topic-edit-form">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      maxLength={140}
                      className="bg-slate-50 border-slate-200 rounded-none h-11 font-display font-black text-2xl uppercase tracking-tight"
                      data-testid="community-topic-edit-title"
                    />
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={6}
                      maxLength={8000}
                      className="bg-slate-50 border-slate-200 rounded-none resize-none"
                      data-testid="community-topic-edit-body"
                    />
                    <div className="flex items-center justify-between">
                      <div className="font-mono-num text-[10px] text-slate-500">{editBody.length} / 8000</div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditTopic}
                          className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800 h-9 uppercase tracking-widest text-xs font-semibold"
                          data-testid="community-topic-edit-cancel"
                        >
                          <XIcon className="w-3.5 h-3.5 mr-1.5" /> Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={saveEditTopic}
                          disabled={savingTopic}
                          className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold h-9 disabled:opacity-50"
                          data-testid="community-topic-edit-save"
                        >
                          <Check className="w-3.5 h-3.5 mr-1.5" /> {savingTopic ? "Saving…" : "Save changes"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h1 className="font-display font-black text-3xl lg:text-4xl uppercase tracking-tight leading-none">
                        {topic.title}
                      </h1>
                      <RoleBadge role={topic.author_role} />
                      {topic.pinned && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[#0D9488]/50 text-[#0D9488] font-mono-num text-[10px] uppercase tracking-widest"
                          data-testid="community-topic-pinned-badge"
                        >
                          <Pin className="w-3 h-3" /> Pinned
                        </span>
                      )}
                      {topic.locked && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[#0D9488]/50 text-[#0D9488] font-mono-num text-[10px] uppercase tracking-widest"
                          data-testid="community-topic-locked-badge"
                        >
                          <Lock className="w-3 h-3" /> Locked
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-mono-num uppercase tracking-widest text-slate-500 mb-4 flex-wrap">
                      <UserNameLink userId={topic.user_id} handle={topic.author_handle} name={topic.author_name} prefix="@" className="text-slate-500 hover:text-[#0D9488]" showUnderline={false} />
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(topic.created_at)}</span>
                      <ReportButton targetType="topic" targetId={topic.id} label="Report post" />
                      {topicCategory && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-num text-[10px] uppercase tracking-widest"
                          style={{ borderColor: topicCategory.color, color: topicCategory.color }}
                          data-testid="community-topic-category-badge"
                        >
                          <Tag className="w-3 h-3" /> {topicCategory.name}
                        </span>
                      )}
                      {topic.edited && (
                        <span className="flex items-center gap-1 text-slate-600" data-testid="community-topic-edited-tag">
                          <Pencil className="w-3 h-3" />edited {timeAgo(topic.updated_at)}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[#0D9488]">
                        <MessageCircle className="w-3 h-3" />{topic.reply_count} {topic.reply_count === 1 ? "reply" : "replies"}
                      </span>
                    </div>
                <div className="text-slate-900 whitespace-pre-wrap leading-relaxed" data-testid="community-topic-body">
                  {topic.body}
                </div>
                {topic.video_url && <VideoEmbed url={topic.video_url} />}
                {topic.attachments?.length > 0 && <TopicAttachments attachments={topic.attachments} />}
                {topic.source?.kind === "playbook_lesson" && topic.source?.playbook_id && topic.source?.lesson_id && (
                  <Link
                    to={`/playbook/${topic.source.playbook_id}?lesson=${topic.source.lesson_id}`}
                    className="mt-3 inline-flex items-center gap-1.5 border border-[#0D9488]/40 hover:border-[#0D9488] bg-[#0D9488]/5 hover:bg-[#0D9488]/10 px-3 h-8 font-mono-num text-[10px] uppercase tracking-widest font-black text-[#0D9488] transition-colors"
                    data-testid="topic-back-to-lesson"
                    title="Open the source lesson in the Playbook"
                  >
                    <BookOpenCheck className="w-3.5 h-3.5" /> Back to Lesson
                  </Link>
                )}
                <ReactionBar
                  target="topic"
                  entity={topic}
                  currentUserId={user?.id}
                  emojis={topic?.source === "weekly_recap" ? RECAP_REACTIONS : undefined}
                  onUpdate={(t) => setTopic(t)}
                />
              </>
            )}
              </div>
              {!editingTopic && (canDeleteTopic || canLockTopic || canPinTopic || canEditTopic) && (
                <div className="flex items-center gap-1 shrink-0 w-full sm:w-auto justify-end order-3 sm:order-none -mt-1 sm:mt-0">
                  {canEditTopic && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditTopic}
                      className="h-8 rounded-none border border-transparent text-slate-500 hover:text-[#0D9488] hover:bg-slate-200 hover:border-[#0D9488]/30"
                      title="Edit topic"
                      data-testid="community-topic-edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {canPinTopic && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={togglePin}
                      className={`h-8 rounded-none border border-transparent hover:bg-slate-200 ${topic.pinned ? "text-[#0D9488] hover:border-[#0D9488]/40" : "text-slate-500 hover:text-[#0D9488] hover:border-[#0D9488]/30"}`}
                      title={topic.pinned ? "Unpin from top" : "Pin to top"}
                      data-testid="community-topic-pin-toggle"
                    >
                      {topic.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                  {isStaff && (
                    <div className="flex items-center border border-slate-200 h-8" title="Mentorship area — appears on the matching page">
                      <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500 px-2">Mentor</span>
                      {["nutrition", "training", "peptides"].map((t) => {
                        const active = topic.mentorship_topic === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setMentorship(active ? null : t)}
                            data-testid={`community-topic-mentor-${t}`}
                            className={[
                              "h-8 px-2 font-mono-num text-[10px] uppercase tracking-widest border-l border-slate-200 transition-colors",
                              active ? "bg-[#0D9488] text-black font-semibold" : "text-slate-500 hover:text-[#0D9488]",
                            ].join(" ")}
                          >
                            {t[0].toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {canLockTopic && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleLock}
                      className={`h-8 rounded-none border border-transparent hover:bg-slate-200 ${topic.locked ? "text-[#0D9488] hover:border-[#0D9488]/40" : "text-slate-500 hover:text-[#0D9488] hover:border-[#0D9488]/30"}`}
                      title={topic.locked ? "Unlock — allow replies" : "Lock — stop new replies"}
                      data-testid="community-topic-lock-toggle"
                    >
                      {topic.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                  {canDeleteTopic && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDeleteTopic(true)}
                      className="h-8 rounded-none text-slate-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      data-testid="community-topic-delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Replies */}
          <div className="mb-6" data-testid="community-replies">
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-3">
              // Replies ({replies.length})
            </div>
            {replies.length === 0 ? (
              <div className="border border-slate-200 p-8 text-center text-slate-500 text-sm font-mono-num uppercase tracking-widest text-xs">
                No replies yet. Say something.
              </div>
            ) : (
              <div className="space-y-3">
                {replies.map((r) => {
                  const isEditing = editingReplyId === r.id;
                  return (
                    <div
                      key={r.id}
                      className="border border-slate-200 bg-white p-4 flex items-start gap-3 group"
                      data-testid={`community-reply-${r.id}`}
                    >
                      <UserNameLink userId={r.user_id} handle={r.author_handle} name={r.author_name} testid={`community-reply-avatar-link-${r.id}`}>
                        <Avatar name={r.author_name} role={r.author_role} avatarUrl={r.author_avatar_url} />
                      </UserNameLink>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <UserNameLink userId={r.user_id} handle={r.author_handle} name={r.author_name} prefix="@" className="font-mono-num text-xs text-slate-800 font-semibold" showUnderline={false} />
                          <RoleBadge role={r.author_role} />
                          <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">{timeAgo(r.created_at)}</span>
                          <ReportButton targetType="reply" targetId={r.id} compact testid={`community-reply-report-${r.id}`} />
                          {r.edited && !isEditing && (
                            <span
                              className="font-mono-num text-[10px] text-slate-600 uppercase tracking-widest flex items-center gap-1"
                              data-testid={`community-reply-edited-tag-${r.id}`}
                            >
                              <Pencil className="w-2.5 h-2.5" />edited {timeAgo(r.updated_at)}
                            </span>
                          )}
                        </div>
                        {/* Parent-reply chip when this reply targets a specific comment */}
                        {r.parent_reply_id && r.parent_author_name && !isEditing && (
                          <div
                            className="mb-2 inline-flex items-center gap-1.5 border-l-2 border-[#0D9488]/40 pl-2 py-0.5 text-[11px] text-slate-600"
                            data-testid={`community-reply-parent-chip-${r.id}`}
                          >
                            <CornerDownRight className="w-3 h-3 text-[#0D9488]" />
                            <span>replying to</span>
                            <UserNameLink userId={r.parent_user_id} handle={r.parent_author_handle} name={r.parent_author_name} prefix="@" className="font-mono-num text-slate-900" showUnderline={false} />
                          </div>
                        )}
                        {isEditing ? (
                          <div className="space-y-2 mt-2" data-testid={`community-reply-edit-form-${r.id}`}>
                            <div className="relative">
                              <Textarea
                                ref={editReplyRef}
                                value={editReplyBody}
                                onChange={(e) => setEditReplyBody(e.target.value)}
                                rows={3}
                                maxLength={8000}
                                className="bg-slate-50 border-slate-200 rounded-none resize-none text-sm pr-12"
                                data-testid={`community-reply-edit-input-${r.id}`}
                              />
                              <div className="absolute right-2 top-2">
                                <EmojiPickerButton
                                  testid={`emoji-reply-edit-${r.id}`}
                                  onPick={(e) => insertAtCursor(editReplyRef.current, editReplyBody, e, setEditReplyBody)}
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="font-mono-num text-[10px] text-slate-500">{editReplyBody.length} / 8000</div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={cancelEditReply}
                                  className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800 h-8 uppercase tracking-widest text-xs font-semibold"
                                  data-testid={`community-reply-edit-cancel-${r.id}`}
                                >
                                  <XIcon className="w-3 h-3 mr-1.5" /> Cancel
                                </Button>
                                <Button
                                  type="button"
                                  onClick={saveEditReply}
                                  disabled={savingReply}
                                  className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold h-8 disabled:opacity-50"
                                  data-testid={`community-reply-edit-save-${r.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1.5" /> {savingReply ? "Saving…" : "Save"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="text-slate-900 whitespace-pre-wrap leading-relaxed text-sm">{r.body}</div>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <ReactionBar
                                target="reply"
                                entity={r}
                                currentUserId={user?.id}
                                emojis={topic?.source === "weekly_recap" ? RECAP_REACTIONS : undefined}
                                onUpdate={(nr) => setReplies((rs) => rs.map((x) => x.id === nr.id ? nr : x))}
                              />
                              {canReply && r.user_id !== user?.id && (
                                <button
                                  type="button"
                                  onClick={() => startReplyTo(r)}
                                  className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-slate-500 hover:text-[#0D9488] transition-colors"
                                  data-testid={`community-reply-reply-btn-${r.id}`}
                                >
                                  <ReplyIcon className="w-3 h-3" /> Reply
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {!isEditing && (canEditReply(r) || canDeleteReply(r)) && (
                        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEditReply(r) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditReply(r)}
                              className="h-7 rounded-none text-slate-500 hover:text-[#0D9488] hover:bg-slate-200"
                              title="Edit reply"
                              data-testid={`community-reply-edit-${r.id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                          )}
                          {canDeleteReply(r) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmDeleteReply(r)}
                              className="h-7 rounded-none text-slate-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid={`community-reply-delete-${r.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div ref={scrollAnchorRef} />
          </div>

          {/* Reply box */}
          {canReply ? (
            <form
              onSubmit={submitReply}
              className="border border-slate-200 bg-white p-4"
              data-testid="community-reply-form"
            >
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center justify-between">
                <span>// Reply as @{user?.name}</span>
                {topic.locked && user?.role === "admin" && (
                  <span className="text-[#0D9488] flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Locked · admin override
                  </span>
                )}
              </div>
              {replyingTo && (
                <div
                  className="mb-3 flex items-start justify-between gap-3 border-l-2 border-[#0D9488] bg-[#0D9488]/5 px-3 py-2"
                  data-testid="community-replying-to-chip"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488] flex items-center gap-1.5 mb-0.5">
                      <CornerDownRight className="w-3 h-3" /> Replying to @{replyingTo.author_name}
                    </div>
                    <div className="text-xs text-slate-600 line-clamp-2 leading-snug">
                      {replyingTo.body}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    className="text-slate-500 hover:text-slate-900 shrink-0"
                    aria-label="Cancel reply"
                    data-testid="community-replying-to-cancel"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="relative">
                <MentionAutocomplete
                  textareaRef={replyRef}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  maxLength={8000}
                  placeholder={replyingTo ? `Reply to @${replyingTo.author_name}…` : "Add to the conversation… type @ to mention"}
                  className="flex min-h-[80px] w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0D9488] resize-none mb-3 pr-12 rounded-none text-slate-900"
                  data-testid="community-reply-input"
                />
                <div className="absolute right-2 top-2">
                  <EmojiPickerButton
                    testid="emoji-reply"
                    onPick={(e) => insertAtCursor(replyRef.current, reply, e, setReply)}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="font-mono-num text-[10px] text-slate-500">{reply.length} / 8000</div>
                <Button
                  type="submit"
                  disabled={posting || !reply.trim()}
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold disabled:opacity-50"
                  data-testid="community-reply-submit"
                >
                  <Send className="w-3.5 h-3.5 mr-2" />
                  {posting ? "Posting…" : "Reply"}
                </Button>
              </div>
            </form>
          ) : (
            <div
              className="border border-[#0D9488]/30 bg-[#0D9488]/5 p-5 flex items-start gap-3"
              data-testid="community-reply-locked-notice"
            >
              <Lock className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" />
              <div>
                <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Topic locked</div>
                <div className="text-sm text-slate-800">Replies are disabled by an admin. You can still read the discussion, but new replies cannot be added.</div>
              </div>
            </div>
          )}

          <AlertDialog open={confirmDeleteTopic} onOpenChange={setConfirmDeleteTopic}>
            <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display uppercase tracking-tight">Delete this topic?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600">
                  All {topic.reply_count || 0} replies will be permanently removed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doDeleteTopic} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="community-topic-delete-confirm">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={!!confirmDeleteReply} onOpenChange={(o) => !o && setConfirmDeleteReply(null)}>
            <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display uppercase tracking-tight">Delete this reply?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600 line-clamp-3">
                  "{confirmDeleteReply?.body}"
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doDeleteReply} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="community-reply-delete-confirm">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </AppLayout>
  );
}
